import {
  DocumentReference,
  TypeGuard,
  isINamedDocument,
  isObject,
  type INamedDocument,
  type ISoftDelete,
  type Timestamp,
} from '@principle-theorem/shared';
import { isBoolean } from 'lodash';
import { type IStatusHistory } from '../common';
import { AccPatientFormType, PatientFormType } from '../patient/patient';
import {
  ICustomFormConfiguration,
  IJsonSchemaFormWithResolverConfig,
} from './custom-form-config';
import { IAccClaimFundingRequest } from '../transaction/funding-request/acc/acc-claim';

export enum PatientFormCollection {
  SubmittedFormHistory = 'submittedFormHistory',
  PatientFormInteractions = 'patientFormInteractions',
}

export enum PatientFormStatus {
  Issued = 'issued',
  Submitted = 'submitted',
  Confirmed = 'confirmed',
}

interface IPatientFormBase<T extends object> extends ISoftDelete {
  status: PatientFormStatus;
  statusHistory: IStatusHistory<PatientFormStatus>[];
  form: IPatientFormSchema<T>;
  template?: INamedDocument<ICustomFormConfiguration>;
}

interface IAccPatientForm<T extends object> extends IPatientFormBase<T> {
  formType: AccPatientFormType;
  associatedDocument?: DocumentReference<IAccClaimFundingRequest>;
}

interface IStandardPatientForm<T extends object> extends IPatientFormBase<T> {
  formType: PatientFormType;
}

export type IPatientForm<T extends object = object> =
  | IAccPatientForm<T>
  | IStandardPatientForm<T>;

export function isStandardPatientForm<T extends object = object>(
  form: unknown
): form is IStandardPatientForm<T> {
  return TypeGuard.interface<IStandardPatientForm<T>>({
    deleted: isBoolean,
    status: TypeGuard.enumValue(PatientFormStatus),
    statusHistory: TypeGuard.arrayOf(isObject),
    formType: TypeGuard.enumValue(PatientFormType),
    template: TypeGuard.undefinedOr(isINamedDocument),
    form: isObject,
  })(form);
}

export function isAccPatientForm<T extends object = object>(
  form: unknown
): form is IAccPatientForm<T> {
  return TypeGuard.interface<IAccPatientForm<T>>({
    deleted: isBoolean,
    status: TypeGuard.enumValue(PatientFormStatus),
    statusHistory: TypeGuard.arrayOf(isObject),
    formType: TypeGuard.enumValue(AccPatientFormType),
    template: TypeGuard.undefinedOr(isINamedDocument),
    form: isObject,
    associatedDocument: TypeGuard.undefinedOr(isObject),
  })(form);
}

export function isPatientForm<T extends object = object>(
  form: unknown
): form is IPatientForm<T> {
  return isStandardPatientForm(form) || isAccPatientForm(form);
}

export interface IPatientFormSchema<T extends object = object>
  extends IJsonSchemaFormWithResolverConfig {
  date?: Timestamp;
  data?: T;
}
